/**
 * ListInput — input reusable para listas de strings con add/remove.
 *
 * Doc 24 §3 Fase 7 — usado en `AiInstructionsBlock` para must_include /
 * must_avoid bullets, y en `ai_global_instructions` del doc-level
 * (Fase 11). Eventualmente también puede usarse para enum_values y
 * otras listas v2.
 *
 * Diseño minimal — no drag-and-drop por ahora (orden por inserción).
 * Si en el futuro hay necesidad, agregamos handle de drag con
 * `dnd-kit/sortable` ya disponible en deps del proyecto.
 */

import { Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface ListInputProps {
  /** Lista actual. Vacía → muestra solo el input para agregar. */
  items: string[]
  /** Callback cuando cambia la lista (add/update/remove). El padre maneja
   * el state — este componente es controlled. */
  onChange(next: string[]): void
  /** Placeholder del input de "agregar". */
  placeholder?: string
  /** Etiqueta del botón "agregar". */
  addLabel?: string
  /** Máximo de items permitidos. Si se alcanza, el input de agregar
   * se deshabilita. Default: sin límite. */
  maxItems?: number
  /** Máximo de caracteres por item. Default: 400. */
  maxItemLength?: number
  /** Modo readonly — oculta inputs editables y muestra solo la lista. */
  readOnly?: boolean
  /** ID estable para tests / a11y. */
  testId?: string
}

export function ListInput({
  items,
  onChange,
  placeholder,
  addLabel,
  maxItems,
  maxItemLength = 400,
  readOnly = false,
  testId,
}: ListInputProps) {
  const { t } = useTranslation('templates')
  const [draft, setDraft] = useState('')

  const canAdd = !readOnly && (!maxItems || items.length < maxItems)
  const canSubmit = canAdd && draft.trim().length > 0

  function submit() {
    if (!canSubmit) return
    const next = [...items, draft.trim()]
    onChange(next)
    setDraft('')
  }

  function updateItem(idx: number, value: string) {
    if (readOnly) return
    const next = items.map((item, i) => (i === idx ? value : item))
    onChange(next)
  }

  function removeItem(idx: number) {
    if (readOnly) return
    onChange(items.filter((_, i) => i !== idx))
  }

  return (
    <div className="space-y-2" data-testid={testId}>
      {items.length > 0 ? (
        <ul className="space-y-1.5">
          {items.map((item, idx) => (
            <li
              key={idx}
              className="flex items-start gap-2 group"
              data-testid={testId ? `${testId}-item-${idx}` : undefined}
            >
              <span
                className="text-ink-subtle text-sm pt-2 select-none"
                aria-hidden
              >
                ·
              </span>
              <input
                type="text"
                value={item}
                onChange={(e) => updateItem(idx, e.target.value)}
                maxLength={maxItemLength}
                disabled={readOnly}
                className="flex-1 bg-canvas border border-line rounded-md px-2.5 py-1.5 text-sm font-body focus:outline-none focus:border-brand disabled:opacity-60"
              />
              {!readOnly ? (
                <button
                  type="button"
                  onClick={() => removeItem(idx)}
                  aria-label={t('designer.editor.list_input_remove_item') as string}
                  className="text-ink-subtle hover:text-critical p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={
                    testId ? `${testId}-remove-${idx}` : undefined
                  }
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {canAdd ? (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                submit()
              }
            }}
            placeholder={placeholder}
            maxLength={maxItemLength}
            className="flex-1 bg-canvas border border-line rounded-md px-2.5 py-1.5 text-sm font-body focus:outline-none focus:border-brand"
            data-testid={testId ? `${testId}-add-input` : undefined}
          />
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-line text-ink hover:bg-surface-muted disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={testId ? `${testId}-add-button` : undefined}
          >
            <Plus className="w-3.5 h-3.5" />
            {addLabel ?? t('designer.editor.list_input_add_default')}
          </button>
        </div>
      ) : null}

      {maxItems && items.length >= maxItems && !readOnly ? (
        <p className="text-[11px] text-ink-subtle">
          {t('designer.editor.list_input_max_reached', { max: maxItems })}
        </p>
      ) : null}
    </div>
  )
}
